import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings } from 'lucide-react';
import type { NotificationItem } from '@/api/notifications';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import NotificationPreferences from './NotificationPreferences';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function getEntityPath(entityType: string, entityId: string, role?: string): string | null {
  // LEAD / TECH run the mobile shop-floor shell, which has no /jobs/:id detail
  // route — their job surface is the floor view. Routing them to /jobs/:id
  // dead-ends at the catch-all redirect, so send them to /jobs/:id/floor instead.
  const isFloorRole = role === 'LEAD' || role === 'TECH';
  switch (entityType) {
    case 'JOB':
    case 'INVOICE':
      return isFloorRole ? `/jobs/${entityId}/floor` : `/jobs/${entityId}`;
    case 'INQUIRY':
      return `/inquiries/${entityId}`;
    case 'APPOINTMENT':
      return '/appointments';
    default:
      return null;
  }
}

interface Props {
  notifications: NotificationItem[];
  unreadCount: number;
  soundEnabled: boolean;
  onSoundChange: (enabled: boolean) => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onClose: () => void;
  align?: 'left' | 'right';
}

export default function NotificationPanel({
  notifications,
  unreadCount,
  soundEnabled,
  onSoundChange,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
  align = 'right',
}: Props) {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [showPrefs, setShowPrefs] = useState(false);
  const positionClass = align === 'left' ? 'left-0' : 'right-0';

  const handleClick = (notif: NotificationItem) => {
    if (!notif.read) onMarkAsRead(notif.id);
    const path = getEntityPath(notif.entityType, notif.entityId, user?.role);
    if (path) {
      navigate(path);
      onClose();
    }
  };

  if (showPrefs) {
    return (
      <div
        className={`absolute ${positionClass} top-full mt-2 w-80 max-h-[28rem] bg-card rounded-xl shadow-lg ring-1 ring-border z-50 flex flex-col overflow-hidden`}
      >
        <NotificationPreferences
          soundEnabled={soundEnabled}
          onSoundChange={onSoundChange}
          onClose={() => setShowPrefs(false)}
        />
      </div>
    );
  }

  return (
    <div
      className={`absolute ${positionClass} top-full mt-2 w-80 max-h-[28rem] bg-card rounded-xl shadow-lg ring-1 ring-border z-50 flex flex-col overflow-hidden`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <span className="text-sm font-semibold text-foreground">Notifications</span>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button onClick={onMarkAllAsRead} className="text-xs text-blue-600 hover:underline">
              Mark all as read
            </button>
          )}
          <button
            onClick={() => setShowPrefs(true)}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Notification settings"
          >
            <Settings size={14} />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No notifications</p>
        ) : (
          notifications.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-accent transition-colors ${
                !n.read ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''
              }`}
            >
              <div className="flex items-start gap-2">
                {!n.read && (
                  <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                )}
                <div className={!n.read ? '' : 'pl-4'}>
                  <p className="text-sm font-medium text-foreground leading-tight">{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{n.body}</p>
                  <p className="text-[10px] text-muted-foreground/70 mt-1">
                    {timeAgo(n.createdAt)}
                  </p>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
